<template>
  <v-container class="px-6">
    <div class="pr-2 py-2">
      <h2 class="common-text-alignment d-flex justify-space-between align center">
        Groups
        <v-btn icon @click.stop="onCloseDrawer">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </h2>
    </div>
    <div class="body-1 my-6">
      Rename the groups here.
    </div>
    <v-form
      ref="groupForm"
      v-model="valid"
      lazy-validation
    >
      <v-row
        v-for="(value, key) in group"
        :key="key"
      >
        <v-col cols="4" class="pb-0">
          <div class="pb-5 pt-4 pr-5 text-right">
            <label class="secondary--font label-alignment font-weight-600">{{ formatLabel(key) }}</label>
          </div>
        </v-col>
        <v-col cols="8" class="pb-0 pl-0">
          <v-text-field
            v-model.trim="group[key]"
            outlined
            class="body-1 text-field mr-4"
            placeholder="Text here..."
            :rules="[...labelRules, duplicateNameCheck(key)]"
            @input="validateField"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="px-6 mt-10">
          <v-btn
            v-track="'erfm-group-rename-cancel'"
            outlined
            @click="onCloseDrawer"
          >
            Cancel
          </v-btn>
          <v-btn
            v-track="'erfm-group-rename-reset'"
            class="ml-3"
            :outlined="valid"
            :disabled="!valid"
            @click="onReset"
          >
            Reset
          </v-btn>
          <v-btn
            v-track="'erfm-group-rename-save'"
            class="ml-3"
            :dark="valid && !isChanged"
            :disabled="!valid || isChanged"
            :loading="loading"
            @click="onSubmit"
          >
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import commonFunctions from "@/commonFunctions";
import { $erfmReport } from "@/services/";

export default {
  data() {
    return {
      // Validation state for the form
      valid: false,
      // Group object containing different categories with empty values by default
      group: {},
      // Rules to validate the input fields for group names
      labelRules: [
        v => !!v || "Group name is required", // Group name required rule
        v => !v || !!v.trim() || "Group name cannot be blank", // Ensure no blank group names
        this.emojiRegex(), // Rule to disallow emojis
      ],
      // Loading state to manage submit button behavior
      loading: false,
      isChanged: false,
      preGroupData: {}
    };
  },

  computed: {
    // Map the drawer state from the Vuex store
    ...mapGetters({
      drawer: 'ui/drawer',
    }),
  },
  watch: {
    group: {
      handler() {
       this.isChanged = (
        JSON.stringify(this.group) === JSON.stringify(this.preGroupData)
       )
      },
      deep: true
    }
  },
  mounted() {
    // If there's response data in the drawer, populate the group object with it
    if (this.drawer.right.response) {
      this.group = { ...this.drawer.right.response.group };
      // Remove `total` from the group, as it's used internally for chart representation
      delete this.group.total;
      this.preGroupData = { ...this.group }
    }
  },

  methods: {
    // Validation method to ensure no duplicate group names
    duplicateNameCheck(currentKey) {
      return (v) => {
        const groupValues = Object.keys(this.group)
          .filter(key => key !== currentKey) // Exclude the current field from validation
          .map(key => this.group[key].trim().toLowerCase()); // Convert values to lowercase for case-insensitive check
        return !groupValues.includes(v.trim().toLowerCase()) || "Group name already exists";
      };
    },

    // Method to validate fields on input change
    validateField() {
      this.$refs.groupForm.validate();
    },

    // Method to validate that emojis are not used in the input
    emojiRegex(message = 'You cannot use emojis in this field.') {
      return v => commonFunctions.isEmojiExist(v) || message;
    },

    // Close the right drawer and reset its state in the Vuex store
    onCloseDrawer() {
      this.$store.commit('ui/drawer.right', {
        show: false,
        component: null,
        response: {},
      });
    },

    // Reset group names to formatted default values
    onReset() {
      Object.keys(this.group).forEach(key => {
        // Capitalize and format the group name keys
        this.group[key] = this.formatLabel(key);
      });
    },

    // Submit the updated group names, display success message, and reload the heatmap
    onSubmit() {
      this.loading = true;
      $erfmReport.saveGroupAliases(this.group)
        .then(response => {
          const { data } = response.data;
          // Update prev value with updated data
          this.drawer.right.response.onUpdate(data)
          // Close the drawer after successful save
          this.onCloseDrawer();
          // Show success notification
          this.$store.commit('ui/snackbar', {
            message: 'Group name saved successfully.',
            type: 'success',
          });
          this.preGroupData = { ...data }
        })
        .finally(() => {
          // Stop the loading indicator after the process is complete
          this.loading = false;
        });
    },

    // Helper function to format labels by replacing underscores and capitalizing words
    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    },
  }
};
</script>
<style lang="scss" scoped>
.common-text-alignment{
  letter-spacing: 0.25px;
}
.text-field {
  color: rgba(0, 0, 0, 0.6);
}
::v-deep .v-input:not(.v-input--is-disabled) input {
  letter-spacing: 0.5px;
}
.label-alignment {
  letter-spacing: 0.55px;
}
</style>